import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { selectProgramType } from 'legacy/lut/features/partner360/configuration'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { getUrlWithParams } from 'core-components/helpers/url'
import { Button } from 'core-components/library/button'
import { useNavigate } from 'core/hooks/useNavigate'
import { p360TabsIds } from 'partner360/entities/partnerCompleteness'
import { MdfActivityDetailsEditor } from 'partner360/features/mdfActivities'
import { scrollToElement } from 'partner360/helpers/scrollToElement'
import { useCompletenessItems } from 'partner360/widgets/partnerCompleteness/hooks/useCompletenessItems'

import type { ICockpitExportPayload } from '../../api'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../configs/translations'
import { COCKPIT_MDF_EDIT_CURRENT_FYEAR_ONLY } from '../../constants'
import {
  DEFAULT_CURRENCY,
  formatCockpitScore,
  getCockpitInitiativesInScope,
  getCockpitKeyInitiativesSummary,
  getCockpitMdfActivitiesInScope,
  getRevenueAndSobActuals,
  isCockpitInitiativeInOffering,
  OVERALL_CATEGORY,
} from '../../lib'
import {
  useCockpitExportContext,
  useCockpitKeyInitiativesData,
  useEffectiveActualsSync,
  useFilteredFinSobRows,
  useRefetchPlanningCockpitData,
} from '../../model/hooks'
import {
  selectCockpitCategory,
  selectCockpitCurrency,
  selectCockpitInitiativesPlanQualitySummary,
  selectCockpitInitiativesView,
  selectCockpitMdfSales,
  selectCockpitMetric,
  selectCockpitOffering,
  selectCockpitPeriods,
  selectCockpitPlanningPeriod,
} from '../../model/selectors'
import { CockpitEmptyState } from '../components/cockpitEmptyState/CockpitEmptyState'
import { useCockpitExportSlot, useRegisterCockpitExport } from '../components/cockpitExport'

import {
  CockpitInitiativesQuality,
  CockpitInitiativesQualityHierarchy,
  CockpitInitiativesView,
  CockpitMdfDetail,
  getInitiativesTabLabel,
  INITIATIVES_TABS,
} from './components'
import { CockpitInitiativesRequirements } from './components/CockpitInitiativesRequirements'
import {
  SHOW_MANAGER_COCKPIT_MDF_ACTIVITY_DETAILS,
  USE_COCKPIT_MDF_SALES_FROM_DATA,
} from './components/cockpitMdfDetail/config'
import { useInitiativesQualityScore } from './hooks/useInitiativesQualityScore'

import styles from './CockpitKeyInitiativesExpanded.module.scss'

const TILE_TITLE = 'Initiatives'

/** Names the sub-tab strip in an exported snapshot. */
const VIEW_LABEL = 'View'

const NON_DATA_PROGRAM_TYPES = new Set<ProgramTypes>([
  ProgramTypes.Default,
  ProgramTypes.Summary,
  ProgramTypes.None,
  ProgramTypes.Overall,
  ProgramTypes.Consolidated,
])

const isConcreteProgramType = (value: string): value is ProgramTypes =>
  Object.values(ProgramTypes).includes(value as ProgramTypes) &&
  !NON_DATA_PROGRAM_TYPES.has(value as ProgramTypes)

interface IMdfEditorContext {
  activityId: number
  partnerId: string
  fYear: number
  programType: ProgramTypes
}

/**
 * The tile's expanded content: the initiatives the partner planned and the MDF activities
 * funding them. Initiatives are grouped directly for one partner; the manager cockpit can switch
 * between the full Geo > Product and Product > Geo hierarchies. MDF keeps its grouped chart and
 * records. The view switch stands in the tile header — see `CockpitKeyInitiativesTitle` — and
 * both read the view from the slice.
 */
interface ICockpitKeyInitiativesExpandedProps {
  isManagerCockpitMode?: boolean
  showGeoHierarchy?: boolean
}

export const CockpitKeyInitiativesExpanded = ({
  isManagerCockpitMode = false,
  showGeoHierarchy = false,
}: ICockpitKeyInitiativesExpandedProps = {}) => {
  const navigate = useNavigate()
  const translate = useSafetyTranslation()
  const refetchPlanningCockpitData = useRefetchPlanningCockpitData()
  const { completenessItems } = useCompletenessItems()
  const initiativesPlanItem = completenessItems.find(({ id }) => id === 'initiativesPlan')
  /** Which view the tile was opened onto: the collapsed tile's score opens the quality pillars,
   *  every other way in opens the lists. Kept in the slice because the collapsed and the expanded
   *  view are rendered by the tile board as separate subtrees, and because a widget tour drives
   *  it from the page above both. */
  const activeTab = useSelector(selectCockpitInitiativesView)
  const configuredProgramType = useSelector(selectProgramType)
  const [mdfEditorContext, setMdfEditorContext] = useState<IMdfEditorContext | null>(null)

  const handleOpenInitiativesPlan = useCallback(() => {
    if (!initiativesPlanItem) {
      return
    }

    scrollToElement(initiativesPlanItem.scrollElementId ?? p360TabsIds)

    if (initiativesPlanItem.urlParams) {
      navigate(getUrlWithParams(initiativesPlanItem.urlParams))
    }
  }, [initiativesPlanItem, navigate])

  const exportSlot = useCockpitExportSlot()
  const exportContext = useCockpitExportContext(isManagerCockpitMode)

  const { initiatives, mdfActivities } = useCockpitKeyInitiativesData({
    isManagerCockpitMode,
  })
  const selectedOffering = useSelector(selectCockpitOffering)
  const selectedCategory = useSelector(selectCockpitCategory)
  const selectedPeriods = useSelector(selectCockpitPeriods)
  const selectedMetric = useSelector(selectCockpitMetric)
  const salesFromData = useSelector(selectCockpitMdfSales)
  const selectedCurrency = useSelector(selectCockpitCurrency)
  const planningPeriod = useSelector(selectCockpitPlanningPeriod)
  const { selectedQuality: quality } = useSelector(selectCockpitInitiativesPlanQualitySummary)
  const { currentFYear, kiBusinessPriorities, keyObjectives } = useSelector(selectJBPSettings)
  const isLocalCurrency = selectedCurrency !== DEFAULT_CURRENCY
  const { actualsRows } = useFilteredFinSobRows()
  const { fYear } = useEffectiveActualsSync()
  const isMdfActivityEditingAllowed = !COCKPIT_MDF_EDIT_CURRENT_FYEAR_ONLY || fYear === currentFYear

  useEffect(() => {
    setMdfEditorContext(null)
  }, [fYear])

  const handleOpenMdfActivity = useCallback(
    (activityId: number) => {
      if (!isMdfActivityEditingAllowed) return

      const activity = mdfActivities.find((item) => item.activityId === activityId)

      if (!activity) return

      const programType =
        (isConcreteProgramType(selectedOffering) && selectedOffering) ||
        (activity.programTypes.includes(configuredProgramType) &&
        isConcreteProgramType(configuredProgramType)
          ? configuredProgramType
          : activity.programTypes.find(isConcreteProgramType))

      if (!programType) return

      setMdfEditorContext({
        activityId,
        partnerId: activity.partnerId,
        fYear,
        programType,
      })
    },
    [configuredProgramType, fYear, isMdfActivityEditingAllowed, mdfActivities, selectedOffering]
  )

  const initiativesInScope = useMemo(
    () =>
      getCockpitInitiativesInScope({
        initiatives,
        fYear,
        selectedOffering,
        selectedCategory,
        selectedPeriods,
        planningPeriod,
      }),
    [initiatives, fYear, selectedOffering, selectedCategory, selectedPeriods, planningPeriod]
  )

  /** Planned MDF money is declared on the initiatives, not on the activities, so it comes from
   *  the same summary the collapsed view reads rather than from the activities below. */
  const { mdfPlanned } = useMemo(
    () =>
      getCockpitKeyInitiativesSummary({
        initiatives,
        mdfActivities,
        fYear,
        selectedOffering,
        selectedCategory,
        selectedPeriods,
        selectedMetric,
        selectedCurrency,
        planningPeriod,
      }),
    [
      initiatives,
      mdfActivities,
      fYear,
      selectedOffering,
      selectedCategory,
      selectedPeriods,
      selectedMetric,
      selectedCurrency,
      planningPeriod,
    ]
  )

  const qualityScore = useInitiativesQualityScore()

  const activitiesInScope = useMemo(
    () =>
      getCockpitMdfActivitiesInScope({
        activities: mdfActivities,
        initiatives,
        kiBusinessPriorities,
        selectedOffering,
        selectedPeriods,
        planningPeriod,
      }),
    [
      mdfActivities,
      initiatives,
      kiBusinessPriorities,
      selectedOffering,
      selectedPeriods,
      planningPeriod,
    ]
  )

  const mdfSalesUsd = useMemo<number | null | undefined>(() => {
    if (USE_COCKPIT_MDF_SALES_FROM_DATA) return salesFromData
    if (isManagerCockpitMode) return undefined

    return getRevenueAndSobActuals({
      rows: actualsRows,
      selectedFYear: fYear,
      actualsType: selectedMetric,
      isLocalCurrency: false,
      category: selectedCategory ?? OVERALL_CATEGORY,
    }).sales
  }, [salesFromData, isManagerCockpitMode, actualsRows, fYear, selectedMetric, selectedCategory])

  /** What the tile shows right now: the filters it was read under, the strip that says which
   *  sub-tab is open, and that sub-tab's own snapshot. Plan Quality is a table in the manager
   *  cockpit and a single partner's pillar cards otherwise. */
  const buildExport = useCallback((): ICockpitExportPayload | null => {
    const view = exportSlot.current?.()

    if (!view) {
      return null
    }

    const activeTabConfig = INITIATIVES_TABS.find(({ key }) => key === activeTab)
    /** What each tab wears beside its title — see `CockpitInitiativesViewSwitch`. */
    const figures = {
      initiatives: String(initiativesInScope.length),
      mdf: String(activitiesInScope.length),
      quality: formatCockpitScore(qualityScore),
    }
    const tabLabel = (tab: (typeof INITIATIVES_TABS)[number]) =>
      getInitiativesTabLabel(tab, translate, figures)

    return {
      type: 'initiatives',
      data: {
        title: TILE_TITLE,
        context: exportContext,
        view: {
          ...view,
          controls: [
            {
              label: VIEW_LABEL,
              value: activeTabConfig ? tabLabel(activeTabConfig) : '',
              options: INITIATIVES_TABS.map(tabLabel),
            },
            ...view.controls,
          ],
        },
      },
    }
  }, [
    exportSlot,
    exportContext,
    activeTab,
    qualityScore,
    translate,
    initiativesInScope.length,
    activitiesInScope.length,
  ])

  const hasOfferingInitiatives = initiatives.some((initiative) =>
    isCockpitInitiativeInOffering(initiative, selectedOffering)
  )
  const showRequirements = !hasOfferingInitiatives && activeTab !== 'mdf'

  /** Offered only where the tile draws a view: an empty Plan Quality or an MDF tab with no
   *  activities shows a placeholder, which has nothing to export. */
  const hasExportableView =
    activeTab === 'quality' ? Boolean(quality) : activeTab !== 'mdf' || activitiesInScope.length > 0

  useRegisterCockpitExport(!showRequirements && hasExportableView ? buildExport : null)

  /** The way into the partner's own initiatives plan. The initiatives list carries it at the end
   *  of its Group by row; the other sub-tabs give it a row of its own. */
  const initiativesPlanLink =
    !isManagerCockpitMode && initiativesPlanItem ? (
      <Button variant="text" size="sm" icon="kbase kbase-link" onClick={handleOpenInitiativesPlan}>
        <Translation span text={initiativesPlanItem.label} />
      </Button>
    ) : null

  return (
    <div className={styles.root}>
      {/* The view switch lives in the tile header. */}
      {initiativesPlanLink && activeTab !== 'initiatives' && (
        <div className={styles.tabs}>{initiativesPlanLink}</div>
      )}

      <div className={styles.content}>
        {showRequirements ? (
          isManagerCockpitMode ? (
            <CockpitEmptyState
              icon="kbase kbase-info-alt"
              title={
                <Translation span text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.empty} />
              }
            />
          ) : (
            <CockpitInitiativesRequirements />
          )
        ) : activeTab === 'quality' ? (
          quality ? (
            isManagerCockpitMode ? (
              <CockpitInitiativesQualityHierarchy
                initiatives={initiativesInScope}
                businessPriorities={kiBusinessPriorities}
                selectedMetric={selectedMetric}
                isLocalCurrency={isLocalCurrency}
                selectedPeriods={selectedPeriods}
                planningPeriod={planningPeriod}
                currency={selectedCurrency}
                keyObjectives={keyObjectives}
                exportSlot={exportSlot}
              />
            ) : (
              <CockpitInitiativesQuality
                quality={quality}
                currency={selectedCurrency}
                exportSlot={exportSlot}
              />
            )
          ) : null
        ) : activeTab === 'initiatives' ? (
          <CockpitInitiativesView
            initiatives={initiativesInScope}
            businessPriorities={kiBusinessPriorities}
            selectedMetric={selectedMetric}
            isLocalCurrency={isLocalCurrency}
            selectedPeriods={selectedPeriods}
            planningPeriod={planningPeriod}
            currency={selectedCurrency}
            showGeoHierarchy={showGeoHierarchy}
            keyObjectives={keyObjectives}
            exportSlot={exportSlot}
            actions={initiativesPlanLink}
          />
        ) : activitiesInScope.length ? (
          <CockpitMdfDetail
            activities={activitiesInScope}
            currency={selectedCurrency}
            isUsd={!isLocalCurrency}
            planned={mdfPlanned}
            exportSlot={exportSlot}
            showActivityDetails={!isManagerCockpitMode || SHOW_MANAGER_COCKPIT_MDF_ACTIVITY_DETAILS}
            onActivityClick={
              !isManagerCockpitMode && isMdfActivityEditingAllowed
                ? handleOpenMdfActivity
                : undefined
            }
            salesUsd={mdfSalesUsd}
          />
        ) : (
          <CockpitEmptyState
            icon="kbase kbase-info-alt"
            title={
              <Translation
                span
                text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.mdf.empty}
              />
            }
          />
        )}
      </div>

      {!isManagerCockpitMode && isMdfActivityEditingAllowed && mdfEditorContext && (
        <MdfActivityDetailsEditor
          {...mdfEditorContext}
          onDataChanged={refetchPlanningCockpitData}
          onClose={() => setMdfEditorContext(null)}
        />
      )}
    </div>
  )
}

CockpitKeyInitiativesExpanded.displayName = 'CockpitKeyInitiativesExpanded'
